/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../components/baseclasses/guid.ts" />
/// <reference path="../../components/models/meevo.model.custom.ts" />

module Meevo.Plugins.Discounts {
    "use strict";
    import DiscountTypeEnum = Meevo.Model.DiscountTypeEnum;

    export class DiscountsContext extends Meevo.Components.BaseClasses.DataMaintenanceContextBase {
        private marketPricingReadAccess: Model.SecurityAccessEnum = Model.SecurityAccessEnum.Hide;
        private marketPricingWriteAccess: Model.SecurityAccessEnum = Model.SecurityAccessEnum.Hide;

        constructor(
            appSettings: Meevo.Components.Common.IAppSettings,
            dataService: Meevo.Components.BaseClasses.IDataService,
            modalService: Meevo.Components.Modal.IModalService,
            identityService: Meevo.Components.Common.IIdentityService,
            loggingService: Meevo.Components.Logging.ILoggingService,
            securityChecker: Meevo.Components.Common.ISecurityService,
            trackingService: Meevo.Components.Tracking.ITrackingService,
            discountsService: Meevo.Plugins.Discounts.IDiscountsService,
            protected busyService: Meevo.Components.Busy.IBusyService
        ) {
            super(appSettings, dataService, modalService, identityService, loggingService, securityChecker, trackingService, null, false);

            this.baseEndpoint = appSettings.API_ENDPOINTS.discount;
            this.sectionTemplatePath = "app/plugins/discounts/sectionViews/";
            this.entityType = 'Discount';

            this.buildCrudSecurityKeys(this.securityKeys.REGISTER_DISCOUNTS_TAB_MAIN);

            const mainReadKey = this.securityKeys.REGISTER_DISCOUNTS_TAB_MAIN + this.securityParts.KEY_PART_READ;

            if (this.securityChecker.hasSecurityDenyOrGrant(mainReadKey)) {
                this.sections.push(new MainSection(
                    0,
                    "SECTION_MAIN",
                    "main.html",
                    true,
                    false,
                    this.baseEndpoint,
                    "",
                    this.loggingService,
                    this.dataService,
                    discountsService,
                    busyService,
                    identityService,
                    Meevo.Model.NounEnum.DiscountMain,
                    modalService,
                    mainReadKey));
            }

            this.loadMarketPricingAccess();
        }

        initializeSections(): void {
        }

        /**
         * Caches the market pricing access levels the header button states read.
         * checkSecurityItem rejects when a key is missing, so the stop is in finally.
         */
        private loadMarketPricingAccess(): void {
            const marketPricingKey = this.securityKeys.MANAGEMENT_MARKETPRICING_TAB_DISCOUNTS;

            this.busyService.start();

            this.securityChecker.checkSecurityItem(marketPricingKey + this.securityParts.KEY_PART_READ)
                .then((item: Model.SecurityItem) => {
                    this.marketPricingReadAccess = item.securityAccess;

                    return this.securityChecker.checkSecurityItem(marketPricingKey + this.securityParts.KEY_PART_WRITE);
                })
                .then((item: Model.SecurityItem) => {
                    this.marketPricingWriteAccess = item.securityAccess;
                })
                .finally(() => this.busyService.stop());
        }

        initializeHeader(model: any): void {
            this.headerContent = model.displayName;
        }

        isMarketPricingButtonVisible() {
            return this.isReadonly && this.isApplicationModeCO() && this.isMarketPricingEnabled() && this.hasDiscountAmount()
                && this.marketPricingReadAccess != Model.SecurityAccessEnum.Hide;
        }

        isMarketPricingButtonDisabled() {
            return !(this.isReadonly && this.isApplicationModeCO() && this.isMarketPricingEnabled()
                && this.marketPricingReadAccess == Model.SecurityAccessEnum.Grant);
        }

        isMarketPricingEnabled() {
            return this.securityChecker.hasSecurityDenyOrGrant(this.securityKeys.MANAGEMENT_MARKETPRICING + this.securityParts.KEY_PART_READ)
        }

        /**
         * Market pricing overrides the single discount amount, so the icon only
         * belongs beside the types that render one.
         */
        private hasDiscountAmount(): boolean {
            const model = this.castModel<Model.IDiscount>();

            if (!model || model.isBuyGet) {
                return false;
            }

            return model.discountTypeEnum === DiscountTypeEnum.PercentOff
                || model.discountTypeEnum === DiscountTypeEnum.AmountOff
                || model.discountTypeEnum === DiscountTypeEnum.SpecialPrice;
        }

        showEmptyModal() {
            const options = this.createModalOptions([]);

            this.modalService.showCustom(options);
        }

        openMarketsModalForDiscount() {
            if (!this.isReadonly) {
                return
            }

            let model = this.castModel<Model.IDiscount>();

            if (this.marketPricingWriteAccess == Model.SecurityAccessEnum.Hide) {
                this.showEmptyModal();
                return;
            }

            this.dataService.customMethodData("api/Discount/" + model.id + "/MarketPricing/List", "GET", null).then((res) => {
                let data = res.data;
                if (data) {
                    const options = this.createModalOptions(data);

                    this.modalService.showCustom(options).then((res) => {
                        /* Saving market prices stamps the Discount_T header, so the model in hand is
                           now a version behind and the next save would trip the concurrency check.
                           Re-reading also brings back the new amount, so nothing is patched by hand. */
                        if (res) {
                            this.reloadSelectedSection();
                        }
                    });
                }
            });
        }

        createModalOptions(data: any): Meevo.Components.Modal.IModalOptions {
            let model = this.castModel<Model.IDiscount>();

            const options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/components/controls/discountMarkets/discountMarkets.template.html",
                controller: "meevo.components.controls.discountMarkets.DiscountMarketsController",
                controllerAs: "vm",
                bindToController: true,
                locals: {
                    options: {
                        data,
                        displayName: model.displayName,
                        isPercent: model.discountTypeEnum == DiscountTypeEnum.PercentOff,
                        discountId: model.id
                    }
                }
            }

            return options;
        }

        isSortByProductsVisible() {
            return !(this.selectedSection as Meevo.Plugins.Discounts.MainSection).discountEntityAllSelected;
        }

        save(): angular.IPromise<any> {
            const model = this.selectedSection.model as Meevo.Model.IDiscount;
            
            if (!model.barCode) {
                const section = this.selectedSection as Meevo.Plugins.Discounts.MainSection;

                if (section && section.barCodeRegExp.test(model.displayName) && model.displayName.length < 31) {
                    return section.promptUseDisplayNameForBarcode()
                        .finally(()=>super.save());
                }
            }

            return super.save();
        }
    }
}